/**
 * Recursively flattens a nested object into a single-level object with dot-notation keys.
 * Nested objects are flattened using dot notation (e.g., `{a: {b: 1}}` becomes `{'a.b': 1}`).
 * Arrays are preserved as-is and not flattened.
 *
 * If the input object is null or undefined, returns an empty object (or the provided result object).
 *
 * @param obj - The object to flatten. Can be any object with nested properties, null, or undefined.
 * @param prefix - Optional prefix for keys (used internally for recursion). Defaults to empty string.
 * @param result - Optional accumulator object (used internally for recursion). Defaults to empty object.
 * @returns A flattened object with dot-notation keys for nested properties.
 *
 * @example
 * Basic nested object:
 * flattenObject({ a: 1, b: { c: 2, d: { e: 3 } } })
 * Returns: { a: 1, 'b.c': 2, 'b.d.e': 3 }
 *
 * @example
 * Arrays are preserved as-is:
 * flattenObject({ items: [1, 2, 3], nested: { value: 'test' } })
 * Returns: { items: [1, 2, 3], 'nested.value': 'test' }
 *
 * @example
 * Mixed types and multiple levels:
 * flattenObject({
 *   name: 'John',
 *   age: 30,
 *   address: {
 *     street: '123 Main St',
 *     city: 'New York',
 *     coordinates: { lat: 40.7128, lng: -74.0060 }
 *   },
 *   tags: ['developer', 'engineer']
 * })
 * Returns: {
 *   name: 'John',
 *   age: 30,
 *   'address.street': '123 Main St',
 *   'address.city': 'New York',
 *   'address.coordinates.lat': 40.7128,
 *   'address.coordinates.lng': -74.0060,
 *   tags: ['developer', 'engineer']
 * }
 *
 * @example
 * Null and undefined values are preserved:
 * flattenObject({ a: null, b: { c: undefined, d: 'value' } })
 * Returns: { a: null, 'b.c': undefined, 'b.d': 'value' }
 *
 * @example
 * Empty nested objects are flattened:
 * flattenObject({ a: 1, b: {}, c: { d: 2 } })
 * Returns: { a: 1, 'c.d': 2 }
 *
 * @example
 * Boolean and number values:
 * flattenObject({ active: true, settings: { theme: 'dark', count: 5 } })
 * Returns: { active: true, 'settings.theme': 'dark', 'settings.count': 5 }
 */
export function flattenObject(
  obj: any,
  prefix = '',
  result: Record<string, any> = {}
): Record<string, any> {
  if (obj === null) return result;
  if (obj === undefined) return result;

  Object.entries(obj).forEach(([key, value]) => {
    const flattenedKey = prefix ? `${prefix}.${key}` : key;
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      flattenObject(value, flattenedKey, result);
    } else {
      result[flattenedKey] = value;
    }
  });

  return result;
}

export function mapById<T extends { id: string | number }>(
  arr: T[]
): Record<string | number, T> {
  return arr.reduce(
    (acc, item) => {
      acc[item.id] = item;
      return acc;
    },
    {} as Record<string | number, T>
  );
}

export function mapByUuid<T extends { uuid: string | number }>(
  arr: T[]
): Record<string | number, T> {
  return arr.reduce(
    (acc, item) => {
      acc[item.uuid] = item;
      return acc;
    },
    {} as Record<string | number, T>
  );
}

export function mapBy<T>(arr: T[], key: keyof T): Record<string, T> {
  return arr.reduce(
    (acc, item) => {
      acc[item[key] as string] = item;
      return acc;
    },
    {} as Record<string, T>
  );
}

export function dotKeysToNestedObject(obj: Record<string, any>): any {
  const result: Record<string, any> = {};

  for (const [dotKey, value] of Object.entries(obj)) {
    const keys = dotKey.split('.');
    keys.reduce((res, key, idx) => {
      if (idx === keys.length - 1) {
        res[key] = value;
      } else if (!res[key] || typeof res[key] !== 'object') {
        res[key] = {};
      }
      return res[key];
    }, result);
  }

  return result;
}

export function filterNullOrUndefined(
  obj: Record<string, any>
): Record<string, any> {
  return Object.fromEntries(
    Object.entries(obj).filter(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      ([_, value]) => value !== null && value !== undefined
    )
  );
}

type SnakeToCamelCase<S extends string> = S extends `${infer T}_${infer U}`
  ? `${T}${Capitalize<SnakeToCamelCase<U>>}`
  : S;

type DeepSnakeToCamelCase<T> = T extends object
  ? T extends Array<infer U>
    ? Array<DeepSnakeToCamelCase<U>>
    : {
        [K in keyof T as SnakeToCamelCase<string & K>]: T[K] extends object
          ? DeepSnakeToCamelCase<T[K]>
          : T[K] extends undefined
            ? undefined
            : T[K];
      }
  : T extends undefined
    ? undefined
    : T;

function toCamelCase(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

export function convertSnakeToCamelCase<T>(obj: T): DeepSnakeToCamelCase<T> {
  if (obj === null || obj === undefined) {
    return undefined as DeepSnakeToCamelCase<T>;
  }

  if (Array.isArray(obj)) {
    return obj.map(convertSnakeToCamelCase) as DeepSnakeToCamelCase<T>;
  }

  if (obj && typeof obj === 'object') {
    const result: Record<string, any> = {};

    for (const [key, value] of Object.entries(obj)) {
      const camelKey = toCamelCase(key);
      result[camelKey] = convertSnakeToCamelCase(value);
    }

    return result as DeepSnakeToCamelCase<T>;
  }

  return obj as DeepSnakeToCamelCase<T>;
}
